import { jsx as _jsx } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/cn';
/**
 * **The brand's button, as the default** — TW 44160061, TW 44164351, TW 44164674, TW 44165107.
 *
 * ## Why the base carries the shape and the type
 *
 * Five reports in one week, on five unrelated screens, all said the same thing: the button is
 * square and it is not set in the brand's face. None of them was a careless screen. Until this
 * change the base class list was shadcn's stock `rounded-md … text-sm font-medium`, so a screen
 * got the brand pill **only** by hand-drawing it — and 60-odd places in this repository do,
 * `rounded-pill bg-primary px-6 py-[14px] font-brand text-md font-bold` and its cousins. Every
 * screen that reached for this component instead got the off-the-shelf grey square.
 *
 * PR #572 fixed five of those screens one at a time. That is whack-a-mole against a wrong default,
 * and the sixth screen inherits the square again the day somebody writes it. So the brand's own
 * treatment now lives in the **base**:
 *
 * | | From the design authority |
 * | --- | --- |
 * | `rounded-pill` | `border-radius:var(--radius-pill)` on every text button in `Web App.dc.html`, `Organizer Console.dc.html` and `H2HSpark Admin Console.dc.html` |
 * | `font-brand` | `font-family:var(--font-brand)` on every one of them |
 * | `font-bold` | `font-weight:700` on every one of them |
 *
 * A variant is therefore **colour**, and a size is **padding and scale**. Neither re-declares a
 * corner, which is what let `sm` and `lg` quietly hold `rounded-md` while the base was changed
 * around them.
 *
 * ## What is deliberately not here
 *
 * **A shadow.** The brand style guide renders Primary, Accent and Spark with a soft purple glow,
 * and this component does not draw one, because `Web App.dc.html` sets `box-shadow` on none of its
 * 40-odd pills and neither does any of the hand-drawn instances in this repository. The two
 * surfaces that do want it (`shadow-accent` under a spark call to action) pass it as a class,
 * where a screen-level emphasis belongs.
 */
const buttonVariants = cva('inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-brand text-sm font-bold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', {
    variants: {
        variant: {
            /**
             * **Primary** — `background:var(--brand-primary); color:#fff`, the filled pill that is the
             * one thing a screen wants done. `brightness` rather than `bg-primary/90` on hover: the
             * prototype's pill does not go translucent over whatever is behind it, and on the ivory
             * page an alpha hover reads as the button fading rather than lighting up.
             */
            default: 'bg-primary text-primary-foreground hover:brightness-[1.06]',
            /**
             * **Accent** — Liz Fuchsia. The style guide's second filled pill, for the one action on a
             * screen that is about a *connection* rather than about progress.
             */
            accent: 'bg-brand-accent text-white hover:brightness-[1.06]',
            /**
             * **Spark** — the fuchsia→purple gradient. The style guide draws it with the sparkle
             * glyph, which is the caller's to pass; this is the fill.
             */
            spark: 'bg-spark text-white hover:brightness-[1.06]',
            destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            /**
             * **Secondary, the neutral one** — `border:1.5px solid var(--border-default);
             * background:var(--surface-card); color:var(--text-strong)`.
             *
             * This is the pairing the design actually draws beside a filled pill on the surfaces the
             * reports named: the marketing header's *For event organizers*, the events band's *Browse
             * all events*, and the admin console's row controls. It was shadcn's 1px `--input` border
             * on `--background` — so on the ivory page the button had no fill of its own and half the
             * edge, which is the "plain grey" in every one of Brad's screenshots.
             *
             * **It is not purple.** The triage comments on TW 44160061 and TW 44164674 proposed the
             * brand's purple Secondary for these, and `Web App.dc.html` draws them neutral
             * (`--border-default`, `--text-strong`) at both call sites. The prototype wins on
             * presentation, so the purple outline stays where the design puts it — `brand-outline`,
             * below.
             */
            outline: 'border-[1.5px] border-border bg-card text-foreground hover:bg-muted',
            /**
             * **Secondary, the brand one** — `border:1.5px solid var(--brand-primary);
             * background:var(--surface-card); color:var(--brand-primary)`, which is what the style
             * guide labels *Secondary* and what `Web App.dc.html` draws inside the member app: the
             * meet-list empty state's *Browse everyone*, Your Events' *Browse events*, the admin
             * console's *Spec*.
             *
             * Separate from `outline` rather than replacing it because the design uses both, on
             * different surfaces, and collapsing them would take the purple onto 37 admin-console
             * controls its own prototype draws grey.
             */
            'brand-outline': 'border-[1.5px] border-primary bg-card text-primary hover:bg-brand-50',
            /**
             * **The 10 September prototype's secondary button** — `border:1.5px solid
             * var(--border-default); background:var(--surface-card); color:var(--text-body)`.
             *
             * Since `outline` caught up to 1.5px and `--surface-card`, this differs from it by one
             * thing: the label is set in `--text-body` rather than `--text-strong`. That is the
             * difference the prototype itself draws — §2.15's *Change photo* and *Receipt*, the event
             * header card and the account's *Scan* are quieter than the marketing site's outline
             * actions — and it is exactly the size of difference four passes over the admin console
             * reported as matching and did not, so it stays named rather than rounded away.
             */
            'outline-strong': 'border-[1.5px] border-border bg-card text-text-body hover:bg-muted',
            secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
            /**
             * Left on shadcn's tinted hover rather than given the style guide's purple **Ghost** text
             * treatment. Its 58 call sites across the three consoles are overwhelmingly icon-only
             * controls — a dropdown trigger, a row menu, a close — where "purple bold label" describes
             * nothing that is drawn, and no report names one. `--accent` is already `purple-50`, so
             * the hover is the brand's either way.
             */
            ghost: 'hover:bg-accent hover:text-accent-foreground',
            link: 'text-primary underline-offset-4 hover:underline',
        },
        size: {
            /**
             * The prototype's three text-button scales. `h-auto` throughout, because the padding is
             * the height: the design's pills grow with their words rather than being pinned to a 32 or
             * 36px box, and a fixed height is what made *Receipt* read a size smaller than everything
             * around it.
             *
             * | Size | `Web App.dc.html` |
             * | --- | --- |
             * | `sm` | `font-size:var(--text-sm); padding:9px 16px` |
             * | `default` | `font-size:var(--text-sm); padding:11px 20px` |
             * | `lg` | `font-size:var(--text-md); padding:15px 28px` |
             *
             * `sm` was `h-8 rounded-md px-3 text-xs`. The `text-xs` is the "set a size smaller and in
             * the wrong typeface for a brand control" of TW 44164674: the design has no 12px button,
             * and the site header's actions are this size.
             */
            default: 'h-auto px-5 py-[11px]',
            sm: 'h-auto px-4 py-[9px]',
            lg: 'h-auto px-7 py-[15px] text-md',
            icon: 'h-9 w-9',
            /**
             * Two further scales the member app draws, kept because five screens name them.
             *
             * `pill` is the row control — `padding:9px 15px` on §2.15's *Receipt*, *Event* and *Change
             * photo*; `pill-lg` is the screen control — `padding:12px 18px` on the account header's
             * two connect-code buttons. They no longer restate `rounded-pill font-brand font-bold`,
             * which the base now carries.
             */
            pill: 'h-auto px-[15px] py-[9px]',
            'pill-lg': 'h-auto px-[18px] py-3',
        },
    },
    defaultVariants: { variant: 'default', size: 'default' },
});
export const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button';
    return (_jsx(Component, { ref: ref, className: cn(buttonVariants({ variant, size, className })), ...props }));
});
Button.displayName = 'Button';
export { buttonVariants };
//# sourceMappingURL=button.js.map